import { type ChangeEvent, memo, useState } from 'react';

import { cx, IconButton, Input } from '@bolteu/clay-react';
import { ClearOutlined, Search } from '@bolteu/kalep-react-icons';

export enum SearchInputSizes {
  SMALL = 'small',
  LARGE = 'large',
}

export interface SearchInputProps {
  searchLabel: string;
  clearLabel: string;
  defaultValue?: string;
  onChange: (searchString: string) => void;
  size?: SearchInputSizes;
  className?: string;
}

const SIZE_CLASSNAME: Record<SearchInputSizes, string> = {
  [SearchInputSizes.SMALL]: 'h-9',
  [SearchInputSizes.LARGE]: 'h-12',
};

export const SearchInput = memo(function SearchInput({
  searchLabel,
  clearLabel,
  defaultValue,
  onChange,
  size = SearchInputSizes.LARGE,
  className,
}: SearchInputProps) {
  const [searchedString, setSearchedString] = useState(defaultValue ?? '');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchedString(value);
    onChange(value);
  };

  const handleClear = () => {
    setSearchedString('');
    onChange('');
  };

  return (
    <Input
      value={searchedString}
      onChange={handleChange}
      placeholder={searchLabel}
      className={cx('w-full', SIZE_CLASSNAME[size], className)}
    >
      <Input.Slot>
        <Search className="size-5 text-tertiary" />
      </Input.Slot>
      <Input.Control />
      {searchedString.length ? (
        <Input.Slot
          render={
            <IconButton
              variant="ghost"
              size="sm"
              icon={<ClearOutlined />}
              aria-label={clearLabel}
              onClick={handleClear}
              onMouseDown={(event) => event.preventDefault()}
            />
          }
        />
      ) : null}
    </Input>
  );
});
